import publicAxios, { API_URL } from './PublicAxios';

/**
 * Serviço para chamadas à API pública de booking.
 * Não requer autenticação.
 */

/**
 * Monta a URL pública da foto do colaborador.
 * @param {string} companyUuid - UUID da empresa
 * @param {number} employeeId - ID do colaborador
 * @returns {string} - URL da imagem
 */
export const getEmployeePhotoUrl = (companyUuid, employeeId) => {
    return `${API_URL}/api/booking/${companyUuid}/employee/${employeeId}/image`;
};

/**
 * Obtém informações da empresa para agendamento público.
 * @param {string} companyUuid - UUID da empresa
 * @returns {Promise} - Dados da empresa com filiais, profissionais e serviços
 */
export const getCompanyInfo = (companyUuid) => {
    return publicAxios.get(`/api/booking/${companyUuid}`);
};

export const getEmployeeDashboard = (companyUuid, employeeUuid, accessToken) => {
    return publicAxios.get(`/api/booking/${companyUuid}/employee/${employeeUuid}/dashboard`, {
        params: { accessToken }
    });
};

export const collaboratorPortalAuth = ({ companySlug, companyUuid, employeeUuid, username, password }) => {
    return publicAxios.post('/api/collaborator-portal/auth', { companySlug, companyUuid, employeeUuid, username, password });
};

export const getCollaboratorPortalDashboard = (token, monthReference) => {
    return publicAxios.get('/api/collaborator-portal/dashboard', {
        params: { monthReference },
        headers: { 'X-Collaborator-Portal-Token': token }
    });
};

export const getCollaboratorPortalAgenda = (token, startDate, endDate) => {
    return publicAxios.get('/api/collaborator-portal/agenda', {
        params: { startDate, endDate },
        headers: { 'X-Collaborator-Portal-Token': token }
    });
};

export const getCollaboratorPortalPushPublicKey = () => publicAxios.get('/api/collaborator-portal/push/public-key');

export const subscribeCollaboratorPortalPush = (token, subscription) => publicAxios.post(
    '/api/collaborator-portal/push/subscriptions', subscription,
    { headers: { 'X-Collaborator-Portal-Token': token } }
);

export const unsubscribeCollaboratorPortalPush = (token, endpoint) => publicAxios.delete(
    '/api/collaborator-portal/push/subscriptions',
    { headers: { 'X-Collaborator-Portal-Token': token }, data: { endpoint } }
);

/**
 * O extrato aceita período livre OU mês fechado, nunca os dois. Com período, o backend valida o
 * teto de dias (CollaboratorPortalService.MAX_RANGE_DAYS), incluindo meses de 31 dias.
 * @param {{start?: string, end?: string, monthReference?: string}=} scope - datas em yyyy-MM-dd
 */
const historyParams = (scope) => (
    scope?.start && scope?.end
        ? { startDate: scope.start, endDate: scope.end }
        : { monthReference: scope?.monthReference }
);

export const getCollaboratorPortalScoreHistory = (token, scope) => {
    return publicAxios.get('/api/collaborator-portal/score-history', {
        params: historyParams(scope),
        headers: { 'X-Collaborator-Portal-Token': token }
    });
};

// Token no header para não expor em URL nem em logs de acesso do servidor.
export const getCollaboratorPortalRevenueHistory = (token, scope) => {
    return publicAxios.get('/api/collaborator-portal/revenue-history', {
        params: historyParams(scope),
        headers: { 'X-Collaborator-Portal-Token': token }
    });
};

/**
 * Bloqueios de agenda do próprio colaborador no período.
 * @param {string} token - token do portal
 * @param {{start: string, end: string}} range - datas em yyyy-MM-dd
 */
export const getCollaboratorPortalAgendaBlocks = (token, range) => {
    return publicAxios.get('/api/collaborator-portal/agenda-blocks', {
        params: { startDate: range.start, endDate: range.end },
        headers: { 'X-Collaborator-Portal-Token': token }
    });
};

/**
 * Fecha um período da agenda do próprio colaborador.
 *
 * O dono do bloqueio sai do token no servidor, então o corpo leva só o período, o motivo e,
 * quando ele atende em mais de uma unidade, qual delas.
 */
export const createCollaboratorPortalAgendaBlock = (token, block) => {
    return publicAxios.post('/api/collaborator-portal/agenda-blocks', block, {
        headers: { 'X-Collaborator-Portal-Token': token }
    });
};

/**
 * Últimas visitas do cliente de um atendimento da agenda do colaborador. O servidor recusa
 * atendimento de colega, então o uuid sozinho não abre o histórico de qualquer cliente.
 */
export const getCollaboratorPortalCustomerHistory = (token, appointmentUuid) => {
    return publicAxios.get(`/api/collaborator-portal/appointments/${encodeURIComponent(appointmentUuid)}/customer-history`, {
        headers: { 'X-Collaborator-Portal-Token': token }
    });
};

// Agendamento rápido do portal. O colaborador do agendamento sai do token no servidor,
// por isso nenhuma destas chamadas envia o id dele.

/** Unidades ativas em que o colaborador atende. */
export const getCollaboratorPortalBookingBranches = (token) => {
    return publicAxios.get('/api/collaborator-portal/booking/branches', {
        headers: { 'X-Collaborator-Portal-Token': token }
    });
};

/** Serviços que o colaborador realiza na unidade. */
export const getCollaboratorPortalBookingServices = (token, branchId) => {
    return publicAxios.get('/api/collaborator-portal/booking/services', {
        params: { branchId },
        headers: { 'X-Collaborator-Portal-Token': token }
    });
};

/** Busca de cliente por nome ou telefone (mínimo de 3 caracteres no servidor). */
export const searchCollaboratorPortalCustomers = (token, term) => {
    return publicAxios.get('/api/collaborator-portal/booking/customers', {
        params: { term },
        headers: { 'X-Collaborator-Portal-Token': token }
    });
};

/** Cria o agendamento na agenda do próprio colaborador. */
export const createCollaboratorPortalAppointment = (token, appointment) => {
    return publicAxios.post('/api/collaborator-portal/booking/appointments', appointment, {
        headers: { 'X-Collaborator-Portal-Token': token }
    });
};

/** Reabre um bloqueio criado pelo próprio colaborador. */
export const cancelCollaboratorPortalAgendaBlock = (token, blockId) => {
    return publicAxios.patch(`/api/collaborator-portal/agenda-blocks/${blockId}/cancel`, null, {
        headers: { 'X-Collaborator-Portal-Token': token }
    });
};

/**
 * Lista profissionais de uma filial.
 * @param {string} companyUuid - UUID da empresa
 * @param {number} branchId - ID da filial
 * @returns {Promise} - Lista de profissionais
 */
export const getEmployeesByBranch = (companyUuid, branchId, customer = {}, services = []) => {
    // Documento e telefone são opcionais e servem para esconder quem não atende cliente do
    // clube. O cliente já se identificou na primeira etapa, então nesta o backend consegue
    // saber se ele é assinante; sem eles a lista vem completa, como antes.
    return publicAxios.get(`/api/booking/${companyUuid}/branch/${branchId}/employees`, {
        params: {
            customerDocument: customer.document || undefined,
            customerPhone: customer.phone || undefined,
            serviceIds: services.map(service => service.id).join(',') || undefined
        }
    });
};

/**
 * Busca o colaborador destaque do mês corrente (mais assinaturas vendidas + maior receita
 * de serviços). branchId é opcional - quando informado, restringe o ranking à filial.
 * @param {string} companyUuid - UUID da empresa
 * @param {number} branchId - ID da filial (opcional)
 * @returns {Promise} - Colaborador destaque (204 sem corpo quando não há destaque no período)
 */
export const getMonthlyHighlightEmployee = (companyUuid, branchId) => {
    return publicAxios.get(`/api/booking/${companyUuid}/highlight-employee`, {
        params: branchId ? { branchId } : {}
    });
};

/**
 * Lista serviços de uma filial.
 * @param {string} companyUuid - UUID da empresa
 * @param {number} branchId - ID da filial
 * @returns {Promise} - Lista de serviços
 */
export const getServicesByBranch = (companyUuid, branchId, includeHidden = false) => {
    // includeHidden só é usado pela agenda interna: lá a recepção precisa lançar cortesia,
    // retoque e indicação, que ficam de fora da lista do site.
    return publicAxios.get(`/api/booking/${companyUuid}/branch/${branchId}/services`, {
        params: { includeHidden: includeHidden || undefined }
    });
};

/**
 * Obtém horários disponíveis para um profissional.
 * @param {string} companyUuid - UUID da empresa
 * @param {number} branchId - ID da filial
 * @param {number} employeeId - ID do profissional
 * @param {string} date - Data no formato yyyy-MM-dd
 * @param {number} duration - Duração em minutos (opcional)
 * @returns {Promise} - Lista de slots disponíveis
 */
export const getAvailableSlots = (companyUuid, branchId, employeeId, date, duration = null) => {
    const params = { date };
    if (duration) {
        params.duration = duration;
    }
    return publicAxios.get(`/api/booking/${companyUuid}/branch/${branchId}/employee/${employeeId}/available-slots`, { params });
};

/**
 * Cria um novo agendamento público.
 * @param {string} companyUuid - UUID da empresa
 * @param {object} appointmentData - Dados do agendamento
 * @returns {Promise} - Dados do agendamento criado
 */
export const createAppointment = (companyUuid, appointmentData) => {
    return publicAxios.post(`/api/booking/${companyUuid}/appointments`, appointmentData);
};

export const previewAppointmentPricing = (companyUuid, appointmentData) => {
    return publicAxios.post(`/api/booking/${companyUuid}/appointments/preview`, appointmentData);
};

/**
 * Busca um cliente pelo CPF/documento.
 * @param {string} companyUuid - UUID da empresa
 * @param {string} document - CPF do cliente (com ou sem formatação)
 * @returns {Promise} - Dados do cliente ou 404
 */
export const lookupCustomerByDocument = (companyUuid, document) => {
    return publicAxios.get(`/api/booking/${companyUuid}/customers/lookup`, { params: { document } });
};

/**
 * Busca um agendamento pelo UUID.
 * @param {string} companyUuid - UUID da empresa
 * @param {string} appointmentUuid - UUID do agendamento
 * @param {string} phone - Telefone do cliente para validação
 * @returns {Promise} - Dados do agendamento
 */
export const getAppointmentByUuid = (companyUuid, appointmentUuid, phone) => {
    return publicAxios.get(`/api/booking/${companyUuid}/appointments/${appointmentUuid}`, {
        params: { phone }
    });
};

/**
 * Cancela um agendamento existente.
 * @param {string} companyUuid - UUID da empresa
 * @param {string} appointmentUuid - UUID do agendamento
 * @param {string} customerPhone - Telefone do cliente
 * @param {string} reason - Motivo do cancelamento
 * @returns {Promise}
 */
export const cancelAppointment = (companyUuid, appointmentUuid, customerPhone, reason) => {
    return publicAxios.post(`/api/booking/${companyUuid}/appointments/cancel`, {
        uuid: appointmentUuid,
        customerPhone,
        reason
    });
};

/** Cancela um agendamento validando o CPF usado na consulta pública. */
export const cancelAppointmentByDocument = async (companyUuid, appointmentUuid, customerDocument, reason) => {
    try {
        return await publicAxios.post(`/api/booking/${companyUuid}/appointments/cancel`, {
            uuid: appointmentUuid,
            customerDocument,
            reason
        });
    } catch (error) {
        if (error.response?.status !== 400) {
            throw error;
        }

        // Compatibilidade temporária com o contrato anterior do cancelamento.
        const customerResponse = await lookupCustomerByDocument(companyUuid, customerDocument);
        const phone = customerResponse.data?.phone?.replace(/\D/g, '');
        if (!phone) {
            throw error;
        }

        return cancelAppointment(companyUuid, appointmentUuid, phone, reason);
    }
};

/**
 * @typedef {Object} PublicAppointmentCustomer
 * @property {number} id
 * @property {string=} fullName
 * @property {string=} phone
 */

/**
 * @typedef {Object} PublicAppointmentEmployee
 * @property {number} id
 * @property {string=} fullName
 */

/**
 * @typedef {Object} PublicAppointmentNamedResource
 * @property {number} id
 * @property {string=} name
 */

/**
 * Contrato devolvido por PublicAppointmentResponseDTO. Manter esta definição perto
 * da chamada evita que a tela volte a usar aliases que não existem na API.
 * @typedef {Object} PublicAppointment
 * @property {string} uuid
 * @property {PublicAppointmentCustomer=} customer
 * @property {PublicAppointmentEmployee=} employee
 * @property {PublicAppointmentNamedResource=} service
 * @property {PublicAppointmentNamedResource=} branch
 * @property {string} status
 * @property {string} scheduledDate
 * @property {number=} duration
 * @property {number=} price
 * @property {string=} notes
 * @property {string=} cancellationReason
 * @property {string=} cancellationDate
 */

/**
 * Lista agendamentos de todos os cadastros associados ao telefone.
 * @param {string} companyUuid - UUID da empresa
 * @param {string} phone - Telefone do cliente
 * @returns {Promise<import('axios').AxiosResponse<PublicAppointment[]>>} Lista de agendamentos
 */
export const getAppointmentsByPhone = (companyUuid, phone) => {
    return publicAxios.get(`/api/booking/${companyUuid}/appointments/by-phone`, {
        params: { phone }
    });
};

/**
 * Lista agendamentos pelo CPF usado no cadastro.
 * @param {string} companyUuid - UUID da empresa
 * @param {string} document - CPF somente com números
 * @returns {Promise<import('axios').AxiosResponse<PublicAppointment[]>>} Lista de agendamentos
 */
export const getAppointmentsByDocument = async (companyUuid, document) => {
    try {
        return await publicAxios.get(`/api/booking/${companyUuid}/appointments/by-document`, {
            params: { document }
        });
    } catch (error) {
        if (error.response?.status !== 400) {
            throw error;
        }

        // Durante uma publicação gradual, o backend antigo interpreta "by-document"
        // como UUID e responde que falta o parâmetro phone. O fallback mantém a tela
        // baseada em CPF até todas as instâncias receberem o endpoint novo.
        const customerResponse = await lookupCustomerByDocument(companyUuid, document);
        const phone = customerResponse.data?.phone?.replace(/\D/g, '');
        if (!phone) {
            throw error;
        }

        return getAppointmentsByPhone(companyUuid, phone);
    }
};

export const getWhatsappAppointmentConfirmation = (token) =>
    publicAxios.get(`/api/booking/appointment-confirmation/${token}`);

export const confirmWhatsappAppointment = (token) =>
    publicAxios.post(`/api/booking/appointment-confirmation/${token}/confirm`);

// Avaliação pós-atendimento: o token do link do e-mail é a única credencial.
export const getAppointmentReview = (token) =>
    publicAxios.get(`/api/appointment-reviews/${token}`);

export const submitAppointmentReview = (token, payload) =>
    publicAxios.post(`/api/appointment-reviews/${token}`, payload);
